import { gql, useQuery } from '@apollo/client';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import { keysToCamel } from '@utils/butter';

import { useAuth } from '@providers/AuthProvider';

import BookInPersonAppointment from './BookInPersonAppointment.component';

export interface IBookInPersonAppointmentsContent {
	pageHeader: string;
	pageInfo: string;
	supportDisclaimer: string;
	iframeLoadingDisclaimer: string;
	bookingErrorHtml: string;
}

interface IMember {
	member: {
		affirmingFirstName: string;
		affirmingLastName: string;
		legalFirstName: string;
		legalLastName: string;
		email: string;
		stateOfResidence: string;
	};
}

const MEMBER_QUERY = gql`
	query member($memberId: UUID!) {
		member(id: $memberId) {
			affirmingFirstName
			affirmingLastName
			legalFirstName
			legalLastName
			email
			stateOfResidence
		}
	}
`;

const BookInPersonAppointmentPage: FC = () => {
	const {
		bookInPersonAppointmentContent,
		manageAppointmentsContent,
		newAppointmentBookingContent,
	} = useStaticQuery(
		graphql`
			{
				manageAppointmentsContent: butterPage(slug: { eq: "manage-appointments" }) {
					confirmerrortext
					iframe_loading_disclaimer
				}
				newAppointmentBookingContent: butterPage(slug: { eq: "new-appointment-booking" }) {
					booking_error_html
				}
				bookInPersonAppointmentContent: butterPage(slug: { eq: "book-in-person-appointment" }) {
					page_header
					page_info
					support_disclaimer
				}
			}
		`
	);

	const { userId } = useAuth();

	const { loading: loadingMemberData, data: memberData } = useQuery<IMember, { memberId: string }>(
		MEMBER_QUERY,
		{
			variables: {
				memberId: userId,
			},
		}
	);

	const customerName = `${
		memberData?.member.affirmingFirstName ?? memberData?.member.legalFirstName
	} ${memberData?.member.affirmingLastName ?? memberData?.member.legalLastName}`;

	const parsedPageContent = keysToCamel({
		...bookInPersonAppointmentContent,
		...manageAppointmentsContent,
		...newAppointmentBookingContent,
	}) as IBookInPersonAppointmentsContent;

	return (
		<BookInPersonAppointment
			loadingData={loadingMemberData}
			pageContent={parsedPageContent}
			member={
				!loadingMemberData
					? {
							id: userId,
							name: customerName,
							email: memberData!.member.email,
							stateOfResidence: memberData!.member.stateOfResidence,
					  }
					: undefined
			}
		/>
	);
};

export default BookInPersonAppointmentPage;
